import React from "react";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import SectionHeading from "@/components/SectionHeading";
import { Shield, FileLock2, KeyRound, Network, Activity, Mail, GraduationCap, BadgeCheck, ScanSearch, ArrowRight, Radar, Lock } from "lucide-react";
import { Link } from "react-router-dom";

const ITEMS = [
  { icon: Radar, title: "VAPT Services", aligned: "Offensive Testing",
    desc: "Hands-on web, network, API and mobile penetration testing with prioritised remediation reports.",
    bullets: ["OWASP Top 10 coverage", "Network and API testing", "Re-test cycles included"] },
  { icon: Shield, title: "Endpoint Security", aligned: "Bitdefender Aligned",
    desc: "Modern EDR/XDR for endpoints, servers and workloads with rapid containment and ransomware rollback.",
    bullets: ["Auto-isolation on detection", "Ransomware rollback", "24/7 managed tuning"] },
  { icon: FileLock2, title: "Data Protection (DLP)", aligned: "Safetica · SecuPi · GTB",
    desc: "Stop data exfiltration across endpoints, email and cloud with policy templates aligned to DPDP and ISO.",
    bullets: ["DPDP / ISO templates", "Insider risk analytics", "Encryption & masking"] },
  { icon: KeyRound, title: "Identity Security", aligned: "Axidian Aligned",
    desc: "Privileged access, MFA and identity governance to make identity your strongest control plane.",
    bullets: ["Privileged access vaulting", "Session recording", "MFA for all critical apps"] },
  { icon: Network, title: "Zero Trust Security", aligned: "Zaperon Aligned",
    desc: "ZTNA replacing legacy VPNs — least-privilege access for hybrid workforces, contractors and partners.",
    bullets: ["Identity-first access", "Device posture checks", "Faster than legacy VPN"] },
  { icon: Activity, title: "Managed Security Services", aligned: "24/7 SOC",
    desc: "Round-the-clock monitoring, threat hunting, incident response and executive reporting.",
    bullets: ["Tier 1 + Tier 2 SOC", "MITRE-aligned detections", "Monthly executive reports"] },
  { icon: Mail, title: "Email Security", aligned: "Phishing & BEC Defense",
    desc: "Advanced phishing, BEC and impersonation defense for Microsoft 365 and Google Workspace.",
    bullets: ["BEC + impersonation detection", "URL & attachment sandboxing", "User reporting workflow"] },
  { icon: GraduationCap, title: "Security Awareness", aligned: "GoldPhish Aligned",
    desc: "Continuous phishing simulation, micro-learning and culture metrics that prove progress.",
    bullets: ["Bite-sized micro-learning", "Region-relevant phishing", "Culture dashboards"] },
  { icon: BadgeCheck, title: "Compliance & GRC", aligned: "DPDP / ISO / SOC 2",
    desc: "Policy automation, audit readiness, risk register and DPDP governance support.",
    bullets: ["DPDP readiness assessment", "Policy templates", "Audit evidence collection"] },
  { icon: ScanSearch, title: "Security Audits", aligned: "Architecture & Process",
    desc: "Configuration, posture and process audits aligned to ISO 27001, DPDP and customer-specific frameworks.",
    bullets: ["Cloud + on-prem audit", "Process & policy review", "Board-ready findings"] },
  { icon: Lock, title: "Managed Endpoint Protection", aligned: "MSSP-Operated",
    desc: "We run your EDR end-to-end — onboarding, tuning, alert triage and ongoing response.",
    bullets: ["Onboarding & tuning", "24/7 alert triage", "Quarterly business reviews"] },
];

export default function Solutions() {
  return (
    <div data-testid="solutions-page">
      <PageHero
        eyebrow="Division 01 · Enterprise Security Stack"
        title="An enterprise security stack — operationalised end-to-end"
        description="From offensive testing to identity, data, endpoint and managed operations — every module ships with deployment, tuning and SOC support from the Cosmos Defence team."
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/contact?type=consultation" className="btn-accent" data-testid="solutions-hero-cta">
            Talk to a Solution Architect <ArrowRight size={16} />
          </Link>
          <Link to="/mssp" className="btn-ghost" data-testid="solutions-mssp-cta">
            Managed Security (MSSP)
          </Link>
        </div>
      </PageHero>

      <section className="py-20 bg-white">
        <div className="container-cd">
          <SectionHeading
            eyebrow="Capabilities"
            title="Eleven capabilities. One operating model."
            description="Mix and match the modules that match your maturity — Cosmos Defence designs the rollout, runs the stack and reports to your board."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ITEMS.map((it, i) => (
              <div key={it.title} className="card-cd" data-testid={`solution-item-${i}`}>
                <div className="flex items-start justify-between">
                  <span className="h-12 w-12 grid place-items-center rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-100">
                    <it.icon size={22} className="text-[#3B82F6]" strokeWidth={1.6} />
                  </span>
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-400 text-right max-w-[140px]">{it.aligned}</span>
                </div>
                <h3 className="mt-6 font-display text-xl font-bold text-[#0A1B5E]">{it.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">{it.desc}</p>
                <ul className="mt-5 pt-5 border-t border-slate-100 space-y-2">
                  {it.bullets.map((b) => (
                    <li key={b} className="text-sm text-slate-700 flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#3B82F6] shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
